import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {StudyType, StudyPhase, ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import SetClinicalStudyPhaseDropdown from 'studies/typed/clinical/SetClinicalStudyPhaseDropdown';
import StudyDesignExportButton from 'studies/workspace/design/StudyDesignExportButton';
import AsyncButton, {AsyncButtonLoadingBehavior} from 'ui-library/buttons/AsyncButton';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';

import StudyDesignReviewBadge from './StudyDesignReviewBadge';

export interface StudyDesignEditorHeaderButtonsProps {
  studyId: string;
  studyDesign: {id: string};
  studyPhase: StudyPhase;
  studyType: StudyType;
  refetch: () => Promise<void>;
  handleSave: () => Promise<void>;
  handleCancel: () => void;
  handleEdit: () => void;
  locked: boolean;
  /**
   * User-facing text to provide context on why editing is locked, if available. Design may be locked without
   * an available reason; do not use this value to determine whether the design is locked (use `locked`).
   */
  lockedReason?: string;
  isEditingStudyDesign: boolean;
  isSaveDisabled: boolean;
  isProceedtoExecutionBlocked: boolean;
  isDesignApprovalRequiredToProceed: boolean;
  allowReturnToDesignPhase: boolean;
}

export function StudyDesignEditorHeaderButtons(props: StudyDesignEditorHeaderButtonsProps): JSX.Element {
  return (
    <div className={css(styles.container)}>
      {BenchlingConfig.STUDIES_ENABLE_REVIEW && (
        <StudyDesignReviewBadge
          studyId={props.studyId}
          studyDesign={props.studyDesign}
          sendForReviewValidationError={
            props.isEditingStudyDesign ? 'Study design has unsaved changes.' : null
          }
          /* When the review changes, we need to refetch the query, this should probably be fixed with BNCH-112863 */
          refetch={props.refetch}
        />
      )}
      {
        /* Hide when editing, since the export would not capture the unsaved changes. */
        !props.isEditingStudyDesign && <StudyDesignExportButton studyId={props.studyId} />
      }
      {
        /* Use `key` to avoid reusing HTMLButtonElements. Clicking "Edit" should not focus "Cancel" */
        props.isEditingStudyDesign ? (
          <Button
            key="cancel"
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            size={ButtonSize.SMALL}
            text="Cancel"
            onClick={props.handleCancel}
            disabled={false}
          />
        ) : (
          <Tooltip
            text={props.lockedReason}
            key="edit-tooltip"
            isDisabled={!props.locked || props.lockedReason == null}
          >
            <Button
              key="edit"
              className={css(styles.editButton)}
              icon="edit"
              buttonStyle={ButtonStyle.TERTIARY_BLUE}
              size={ButtonSize.SMALL}
              text="Edit"
              onClick={props.handleEdit}
              disabled={props.locked}
            />
          </Tooltip>
        )
      }
      {props.isEditingStudyDesign && (
        <AsyncButton
          loadingBehavior={AsyncButtonLoadingBehavior.SPINNER}
          buttonStyle={ButtonStyle.PRIMARY}
          size={ButtonSize.SMALL}
          text="Save"
          onClick={props.handleSave}
          disabled={props.isSaveDisabled}
        />
      )}
      {!props.isEditingStudyDesign && (
        /* Hide when editing, since changing the phase will prevent saving the unsaved changes. */
        <SetClinicalStudyPhaseDropdown
          study={{
            id: props.studyId,
            phase: props.studyPhase,
            studyType: props.studyType,
          }}
          refetch={props.refetch}
          isDisabled={props.isProceedtoExecutionBlocked}
          requiresApproval={props.isDesignApprovalRequiredToProceed}
          allowReturnToDesignPhase={props.allowReturnToDesignPhase}
          actionOrigin={ActionOrigin.STUDY_DESIGN_PAGE}
        />
      )}
    </div>
  );
}

export const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: '6px',
    // When a review change occurs, ReviewBadge flashes to loading state on review change that does not
    // preserve its height, causing the whole toolbar to shift.
    minHeight: BenchlingConfig.STUDIES_ENABLE_REVIEW ? '32px' : undefined,
  },
  editButton: {
    flex: 'none',
  },
});
